/**
 * Government Opportunities Dashboard - Step 1 (Enhanced)
 * Entity-aware dashboard for government procurement opportunities
 * Entity Type: govt_opportunity
 * 
 * Features:
 * - Entity-based queries with server-side pagination
 * - Status tracking (watching, bidding, won, lost, expired)
 * - Deadline calendar with notifications
 * - Auto-matching to company capabilities
 * - BI analytics (win rate, pipeline value, time-to-bid)
 * - Export functionality
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge, Select, formatNumber, formatCurrency, formatPercentage, Modal, useFeatureFlag } from '@tracekit/core';
import { useWorkspace, queryEntities, getAPIClient, getProject, getPolicyPack, getEntitySources } from '@tracekit/core';
// Calendar imports
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { Calendar as BigCalendar, dateFnsLocalizer, Event } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CardSkeleton, DashboardSkeleton } from '../components/LoadingSkeleton';
import { SearchProgressIndicator } from '../components/SearchProgressIndicator';
import { calculateMatchScore, batchCalculateMatchScores, updateOpportunityMatchScore } from '../services/opportunityMatcher';
import { OpportunityDetailModal } from '../components/OpportunityDetailModal';
import { ProposalBuilder } from '../components/proposal-management/ProposalBuilder';
import { KeywordRecommendationsModal } from '../components/KeywordRecommendationsModal';
import { SearchAutocomplete } from '../components/SearchAutocomplete';
import { SearchInfoTooltip } from '../components/SearchInfoTooltip';
import { parseSearchQuery, parsedQueryToFilters, validateSearchQuery } from '../utils/advancedSearchParser';
import {
  enhanceOpportunityEntity,
  getDeadlineUrgency,
  getOpportunityValue,
  getProjectIdDisplay,
  getStageBadgeColor,
} from '../utils/opportunityUtils';
import {
  Search,
  Filter,
  RefreshCw,
  Download,
  Calendar,
  Building2,
  DollarSign,
  Target,
  Clock,
  Award,
  AlertTriangle,
  Eye,
  FileText,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Star,
  MapPin,
  Tag,
  Zap,
  ChevronDown,
  ChevronUp,
  X,
  List,
  Sparkles,
  HelpCircle as HelpCircleIcon,
} from 'lucide-react';

// Entity type from Policy Pack v2.0
export interface GovtOpportunityEntity {
  id: string;
  entity_type: string;
  project_id?: string; // Project ID from entity metadata
  data: {
    // Core Identification
    opportunity_id?: string;
    external_id?: string;
    solicitation_number?: string;
    reference_number?: string;
    title: string;
    description?: string;
    
    // Organization & Agency
    organization?: string;
    issuing_agency?: string;
    department?: string;
    sub_agency?: string;
    office?: string;
    agency_code?: string;
    contracting_office?: string;
    
    // Classification
    opportunity_type?: string; // RFP, RFQ, IFB, EOI, etc.
    procurement_method?: string;
    contract_type?: string;
    award_type?: string;
    competition_type?: string;
    set_aside?: string;
    naics_codes?: string[];
    psc_codes?: string[];
    unspsc_codes?: string[];
    commodity_codes?: string[];
    
    // Status & Dates
    status?: string; // open, closed, awarded, expired, cancelled
    posted_date?: string;
    published_date?: string;
    issue_date?: string;
    deadline?: string;
    closing_date?: string;
    response_deadline?: string;
    last_modified_date?: string;
    archive_date?: string;
    award_date?: string;
    
    // Financial
    estimated_value?: number;
    estimated_budget?: number;
    contract_value?: number;
    budget_range_min?: number;
    budget_range_max?: number;
    currency?: string; // USD, CAD, etc.
    funding_source?: string;
    payment_terms?: string;
    value?: number; // Alias for estimated_value/contract_value
    
    // Geographic
    location?: string;
    region?: string;
    country?: string;
    state_province?: string;
    city?: string;
    postal_code?: string;
    place_of_performance?: string;
    geographic_scope?: string;
    
    // Project Details
    scope_of_work?: string;
    project_description?: string;
    project_duration?: string; // Duration in weeks/months
    contract_duration?: string;
    timeline_weeks?: number;
    key_milestones?: string[];
    deliverables?: string[];
    
    // Requirements
    requirements_summary?: string;
    mandatory_requirements?: string[];
    desirable_requirements?: string[];
    technical_requirements?: string[];
    administrative_requirements?: string[];
    commercial_requirements?: string[];
    legal_requirements?: string[];
    qualification_requirements?: string[];
    certification_requirements?: string[];
    insurance_requirements?: string;
    bonding_requirements?: string;
    security_clearance_required?: boolean;
    
    // Evaluation
    evaluation_criteria?: string;
    evaluation_methodology?: string;
    evaluation_weights?: Record<string, number>;
    scoring_criteria?: string;
    selection_criteria?: string;
    
    // Submission
    submission_format?: string;
    submission_method?: string;
    submission_location?: string;
    page_limit?: number;
    word_limit?: number;
    document_requirements?: string[];
    proposal_format?: string;
    number_of_copies?: number;
    
    // Contact Information
    contact_info?: string;
    primary_contact_name?: string;
    primary_contact_email?: string;
    primary_contact_phone?: string;
    technical_contact_name?: string;
    technical_contact_email?: string;
    contracting_officer_name?: string;
    contracting_officer_email?: string;
    
    // Additional
    keywords?: string[];
    tags?: string[];
    service_categories?: string[];
    matching_service_lines?: string[];
    industry_sector?: string;
    target_audience?: string;
    
    // Links & Documents
    url?: string;
    document_url?: string;
    attachment_urls?: string[];
    solicitation_document_url?: string;
    amendment_urls?: string[];
    
    // Source Metadata
    source?: string;
    source_system?: string;
    data_source?: string;
    integration_type?: string;
    normalized?: boolean;
    raw_data?: Record<string, any>;
    
    // Matching & Analysis
    match_score?: number;
    relevance_score?: number;
    win_probability?: number;
    complexity_score?: number;
    risk_level?: 'low' | 'medium' | 'high';
    strategic_importance?: string;
    
    // History
    amendment_count?: number;
    amendment_history?: Array<Record<string, any>>;
    previous_awards?: Array<Record<string, any>>;
    incumbent_vendor?: string;
    num_bidders?: number;
    historical_win_rate?: number;
    
    // Custom tracking fields
    tracking_status?: 'watching' | 'bidding' | 'won' | 'lost' | 'declined';
    days_until_deadline?: number;
    
    // Computed fields
    proposal_stage?: 'EOI' | 'RFP' | 'Stage 1' | 'Stage 2' | 'Stage 3' | 'Unknown'; // Derived from opportunity_type
    duration_display?: string; // Formatted duration string
  };
  created_at?: string;
  updated_at?: string;
}

interface OpportunityStats {
  total: number;
  watching: number;
  bidding: number;
  won: number;
  lost: number;
  pipelineValue: number;
  avgMatchScore: number;
  upcomingDeadlines: number;
  openOpportunities: number;
  trackedOpportunities: number;
  avgDaysUntilDeadline: number | null;
}

export function OpportunitiesDashboard() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const enableCalendarView = useFeatureFlag('enableCalendarView');
  const [searchInput, setSearchInput] = useState(''); // Input field value (what user types)
  const [searchTerm, setSearchTerm] = useState(''); // Actual search term used in query
  
  // Search history stored in localStorage (per project)
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  
  // Load search history when projectId changes
  useEffect(() => {
    if (typeof window !== 'undefined' && projectId) {
      const stored = localStorage.getItem(`opportunity-search-history-${projectId}`);
      setSearchHistory(stored ? JSON.parse(stored) : []);
    } else {
      setSearchHistory([]);
    }
  }, [projectId]);
  
  // Save search to history (only 1-3 words)
  const saveToSearchHistory = (term: string) => {
    if (!term || !term.trim()) return;
    
    const trimmed = term.trim();
    // Only save if 1-3 words
    const wordCount = trimmed.split(/\s+/).length;
    if (wordCount < 1 || wordCount > 3) return;
    
    setSearchHistory((prev) => {
      // Remove duplicates and add to front
      const updated = [trimmed, ...prev.filter((s) => s !== trimmed)];
      // Keep only last 10 searches
      const limited = updated.slice(0, 10);
      
      // Save to localStorage
      if (typeof window !== 'undefined' && projectId) {
        localStorage.setItem(`opportunity-search-history-${projectId}`, JSON.stringify(limited));
      }
      
      return limited;
    });
  };
  
  const [filterSource, setFilterSource] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTrackingStatus, setFilterTrackingStatus] = useState<string>('all');
  const [filterServiceLine, setFilterServiceLine] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [viewMode, setViewMode] = useState<'cards' | 'list' | 'calendar'>('cards');
  const [isMatching, setIsMatching] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<GovtOpportunityEntity | null>(null);
  const [matchScores, setMatchScores] = useState<Map<string, number>>(new Map()); // opportunity_id -> match_score
  const [sortByMatchScore, setSortByMatchScore] = useState(false); // Toggle between original order and match score order
  const [filterMatchScore, setFilterMatchScore] = useState<string>('all'); // Filter by match score: 'all', 'high', 'medium', 'low'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [calendarLocalizer, setCalendarLocalizer] = useState<any>(null);

  // Initialize calendar localizer with locale
  useEffect(() => {
    const initCalendar = () => {
      // Create localizer with a minimal but valid locale object
      // react-big-calendar requires the locales object, but the actual locale data
      // can be minimal since date-fns functions handle most formatting
      const locales = {
        'en-US': {
          code: 'en-US',
          formatDistance: () => '',
          formatRelative: () => '',
          localize: {
            ordinalNumber: (n: number) => n.toString(),
            era: () => ({} as any),
            quarter: () => ({} as any),
            month: () => ({} as any),
            day: () => ({} as any),
            dayPeriod: () => ({} as any),
          },
          formatLong: {
            date: () => 'PP',
            time: () => 'p',
            dateTime: () => 'PPp',
          },
          match: {
            ordinalNumber: () => null,
            era: () => null,
            quarter: () => null,
            month: () => null,
            day: () => null,
            dayPeriod: () => null,
          },
          options: {
            weekStartsOn: 0,
            firstWeekContainsDate: 1,
          },
        } as any,
      };

      const loc = dateFnsLocalizer({
        format,
        parse,
        startOfWeek,
        getDay,
        locales,
      });
      
      setCalendarLocalizer(loc);
    };
    
    if (viewMode === 'calendar' && !calendarLocalizer) {
      initCalendar();
    }
  }, [viewMode, calendarLocalizer]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('[OpportunitiesDashboard] State changed:', {
      isModalOpen,
      selectedOpportunityId: selectedOpportunity?.id,
      selectedOpportunityTitle: selectedOpportunity?.data?.title,
    });
  }, [isModalOpen, selectedOpportunity]);
  const [showProposalPreview, setShowProposalPreview] = useState(false);
  const [proposalOpportunity, setProposalOpportunity] = useState<GovtOpportunityEntity | null>(null);

  // Build filters for query
  const queryFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    
    // Apply source filter
    if (filterSource !== 'all') {
      filters.source = filterSource;
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filters.status = filterStatus;
    }
    
    // Apply tracking status filter
    if (filterTrackingStatus !== 'all') {
      filters.tracking_status = filterTrackingStatus;
    }
    
    // Apply service line filter
    if (filterServiceLine !== 'all') {
      filters.matching_service_lines_in = [filterServiceLine];
    }
    
    // Apply advanced search parsing
    if (searchTerm && searchTerm.trim()) {
      // Validate query syntax
      const validation = validateSearchQuery(searchTerm);
      if (!validation.valid) {
        console.warn('Invalid search query:', validation.error);
        // Fall back to simple search
        filters._search = searchTerm.trim();
      } else {
        // Parse the query
        const parsed = parseSearchQuery(searchTerm);
        const searchFilters = parsedQueryToFilters(parsed);
        
        // Extract _exclude_terms for client-side filtering (don't send to backend)
        const excludeTerms = searchFilters._exclude_terms;
        delete searchFilters._exclude_terms;
        
        // Store exclude terms separately for client-side filtering
        if (excludeTerms) {
          (filters as any)._exclude_terms = excludeTerms;
        }
        
        // Merge search filters into main filters
        Object.assign(filters, searchFilters);
      }
    }
    
    return filters;
  }, [searchTerm, filterSource, filterStatus, filterTrackingStatus, filterServiceLine]);

  // Combined query: Fetch ALL opportunities (for stats) and paginate client-side
  // This avoids duplicate vector search operations
  const {
    data: opportunitiesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['govt-opportunities-all', projectId, searchTerm, filterSource, filterStatus, filterTrackingStatus, filterServiceLine],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      
      // Remove client-side only filters before sending to backend
      const backendFilters = { ...queryFilters };
      delete (backendFilters as any)._exclude_terms;
      
      // Ensure _search_mode is set to 'hybrid' for vector search when search term is present
      if (backendFilters._search && !backendFilters._search_mode) {
        backendFilters._search_mode = 'hybrid';
      }
      
      // Fetch a large number of opportunities (up to 10000) to get accurate stats
      // We'll paginate client-side from this result set
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters: backendFilters,
        limit: 10000, // Fetch up to 10000 for stats calculation
        offset: 0,
      });

      // Extract relevance scores from search metadata if available
      const searchMetadata = (result as any).search_metadata;
      const entityRelevance = searchMetadata?.entity_relevance || {};
      
      const allEntities = result.entities.map((e) => {
        const daysUntilDeadline = e.data.deadline
          ? Math.ceil((new Date(e.data.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          : null;
        
        // Get relevance score for this entity
        const relevance = entityRelevance[e.id];
        
        return {
          id: e.id,
          entity_type: e.entity_type || 'govt_opportunity',
          data: {
            ...(e.data || {}),
            days_until_deadline: daysUntilDeadline,
          } as GovtOpportunityEntity['data'],
          created_at: e.created_at || new Date().toISOString(),
          updated_at: (e as any).updated_at,
          // Add relevance metadata for display
          relevanceScore: relevance?.relevance_score,
          matchType: relevance?.match_type,
        };
      }) as (GovtOpportunityEntity & { relevanceScore?: number; matchType?: string })[];
      
      // Calculate stats from all entities
      const watching = allEntities.filter((o) => o.data.tracking_status === 'watching').length;
      const bidding = allEntities.filter((o) => o.data.tracking_status === 'bidding').length;
      const won = allEntities.filter((o) => o.data.tracking_status === 'won').length;
      const lost = allEntities.filter((o) => o.data.tracking_status === 'lost').length;

      const pipelineValue = allEntities
        .filter((o) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
        .reduce((sum, o) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);

      const upcomingDeadlines = allEntities.filter(
        (o) => o.data.days_until_deadline !== null && 
               o.data.days_until_deadline !== undefined && 
               o.data.days_until_deadline >= 0 && 
               o.data.days_until_deadline <= 30
      ).length;

      // Calculate open opportunities with comprehensive logic
      // Consider an opportunity "open" if:
      // 1. status is explicitly open/active/published (case-insensitive), OR
      // 2. status is null/undefined/empty but has a future deadline (not yet closed), OR
      // 3. status is not a closed state and has a future deadline
      const openOpportunities = allEntities.filter((o) => {
        const status = (o.data.status || '').toLowerCase().trim();
        const hasFutureDeadline = o.data.days_until_deadline !== null && 
                                  o.data.days_until_deadline !== undefined && 
                                  o.data.days_until_deadline >= 0;
        
        // Explicitly open statuses (case-insensitive, handle variations from different sources)
        const openStatuses = ['open', 'active', 'published', 'draft', 'pending', 'available', 'live'];
        if (openStatuses.includes(status)) {
          return true;
        }
        
        // If status is null/undefined/empty but has future deadline, consider it open
        if (!status && hasFutureDeadline) {
          return true;
        }
        
        // If status is not a closed state, consider it open if it has a future deadline
        const closedStates = ['closed', 'awarded', 'expired', 'cancelled', 'completed', 'ended', 'finished', 'terminated'];
        if (status && !closedStates.includes(status) && hasFutureDeadline) {
          return true;
        }
        
        return false;
      }).length;

      const trackedOpportunities = allEntities.filter(
        (o) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
      ).length;

      const deadlines = allEntities
        .map((o) => o.data.days_until_deadline)
        .filter((d): d is number => d !== null && d !== undefined && d >= 0);
      const avgDaysUntilDeadline = deadlines.length > 0
        ? Math.round(deadlines.reduce((sum, d) => sum + d, 0) / deadlines.length)
        : null;
      
      return {
        allEntities, // All entities for client-side pagination
        total: result.total_count,
        stats: {
          watching,
          bidding,
          won,
          lost,
          pipelineValue,
          upcomingDeadlines,
          openOpportunities,
          trackedOpportunities,
          avgDaysUntilDeadline,
        },
      };
    },
    enabled: !!projectId,
  });

  // Client-side pagination from allEntities
  // Filter and sort all opportunities before pagination
  const allOpportunitiesRaw = opportunitiesData?.allEntities || [];
  const allOpportunities = useMemo(() => {
    let filtered = allOpportunitiesRaw;
    
    // Apply NOT term exclusions if present in filters
    const excludeTerms = queryFilters._exclude_terms;
    if (excludeTerms && Array.isArray(excludeTerms) && excludeTerms.length > 0) {
      filtered = filtered.filter(opp => {
        const searchableText = [
          opp.data.title,
          opp.data.description,
          opp.data.organization,
          opp.data.issuing_agency,
          ...(opp.data.keywords || []),
          ...(opp.data.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        
        // Exclude if any NOT term is found
        return !excludeTerms.some(term => 
          searchableText.includes(term.toLowerCase())
        );
      });
    }
    
    // Apply match score filtering
    if (filterMatchScore !== 'all' && matchScores.size > 0) {
      filtered = filtered.filter(opp => {
        const score = matchScores.get(opp.id) ?? opp.data.match_score ?? 0;
        if (filterMatchScore === 'high') {
          return score >= 0.8;
        } else if (filterMatchScore === 'medium') {
          return score >= 0.5 && score < 0.8;
        } else if (filterMatchScore === 'low') {
          return score > 0 && score < 0.5;
        }
        return true;
      });
    }
    
    // Apply sorting: Match score sorting takes priority if enabled
    if (sortByMatchScore && matchScores.size > 0) {
      // Sort by match score (descending), then by original order for ties
      filtered = [...filtered].sort((a, b) => {
        const scoreA = matchScores.get(a.id) ?? a.data.match_score ?? 0;
        const scoreB = matchScores.get(b.id) ?? b.data.match_score ?? 0;
        if (scoreB !== scoreA) {
          return scoreB - scoreA; // Higher score first
        }
        // Maintain original order for ties
        return 0;
      });
    } else if (searchTerm && searchTerm.trim()) {
      // If there's a search term and match score sorting is not enabled, sort to show tracked opportunities first
      filtered = [...filtered].sort((a, b) => {
        const aTracked = a.data.tracking_status === 'watching' || a.data.tracking_status === 'bidding';
        const bTracked = b.data.tracking_status === 'watching' || b.data.tracking_status === 'bidding';
        
        // Tracked opportunities come first
        if (aTracked && !bTracked) return -1;
        if (!aTracked && bTracked) return 1;
        
        // Within tracked/non-tracked groups, maintain original order
        return 0;
      });
    }
    
    return filtered;
  }, [allOpportunitiesRaw, sortByMatchScore, matchScores, queryFilters._exclude_terms, filterMatchScore, searchTerm]);
  
  const totalCount = allOpportunities.length;
  const startIdx = (page - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const opportunities = allOpportunities.slice(startIdx, endIdx);
  const hasMore = endIdx < allOpportunities.length;
  
  // Use stats from the combined query
  const aggregateStatsData = opportunitiesData?.stats;

  // Fetch project and policy pack for search suggestions
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      return await getProject(projectId);
    },
    enabled: !!projectId,
  });

  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: async () => {
      if (!project?.policy_pack_id) return null;
      return await getPolicyPack(project.policy_pack_id);
    },
    enabled: !!project?.policy_pack_id,
  });

  // Extract search suggestions from policy pack or config
  // Use search history instead of suggestions
  const displaySearchHistory = useMemo(() => {
    return searchHistory.slice(0, 3); // Show only last 3 searches
  }, [searchHistory]);

  // Mutation: Update tracking status
  const updateTrackingMutation = useMutation({
    mutationFn: async ({
      entityId,
      trackingStatus,
    }: {
      entityId: string;
      trackingStatus: string;
    }) => {
      const api = getAPIClient();
      // Convert empty string to null to clear tracking status
      const statusValue = trackingStatus === '' ? null : trackingStatus;
      return await api.put(`/entities/${entityId}`, {
        data: { tracking_status: statusValue },
      });
    },
    onMutate: async ({ entityId, trackingStatus }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['govt-opportunities-all', projectId] });

      // Snapshot all previous query data for rollback
      const previousQueries: Record<string, any> = {};
      
      // Get all matching query keys and snapshot them
      const queryCache = queryClient.getQueryCache();
      queryCache.getAll().forEach((query) => {
        const key = query.queryKey;
        if (Array.isArray(key) && key[0] === 'govt-opportunities-all' && key[1] === projectId) {
          previousQueries[JSON.stringify(key)] = query.state.data;
          
          // Optimistically update the combined query
          queryClient.setQueryData(key, (old: any) => {
            if (!old) return old;
            // Update allEntities array
            if (old.allEntities) {
              const updatedEntities = old.allEntities.map((opp: GovtOpportunityEntity) =>
                opp.id === entityId
                  ? { ...opp, data: { ...opp.data, tracking_status: trackingStatus === '' ? undefined : trackingStatus } }
                  : opp
              );
              
              // Recalculate stats with updated entities
              const watching = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'watching').length;
              const bidding = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'bidding').length;
              const won = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'won').length;
              const lost = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'lost').length;
              const trackedOpportunities = updatedEntities.filter(
                (o: GovtOpportunityEntity) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
              ).length;
              
              const pipelineValue = updatedEntities
                .filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
                .reduce((sum: number, o: GovtOpportunityEntity) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);
              
              return {
                ...old,
                allEntities: updatedEntities,
                stats: {
                  ...old.stats,
                  watching,
                  bidding,
                  won,
                  lost,
                  trackedOpportunities,
                  pipelineValue,
                },
              };
            }
            return old;
          });
        }
      });
      
      // Stats are now calculated from allEntities in the combined query
      // The optimistic update above already handles stats recalculation

      // Return a context object with the snapshotted values
      return { previousQueries };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        Object.entries(context.previousQueries).forEach(([keyStr, data]) => {
          const key = JSON.parse(keyStr);
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSuccess: (data, variables) => {
      // Update the query cache with the actual response from the server
      // This ensures the UI reflects the persisted state
      const queryCache = queryClient.getQueryCache();
      let updatedOpportunity: GovtOpportunityEntity | null = null;
      
      queryCache.getAll().forEach((query) => {
        const key = query.queryKey;
        if (Array.isArray(key) && key[0] === 'govt-opportunities-all' && key[1] === projectId) {
          queryClient.setQueryData(key, (old: any) => {
            if (!old) return old;
            // Update allEntities array with the server response
            if (old.allEntities) {
              const updatedEntities = old.allEntities.map((opp: GovtOpportunityEntity) => {
                if (opp.id === variables.entityId) {
                  const updated = { 
                    ...opp, 
                    data: { 
                      ...opp.data, 
                      tracking_status: variables.trackingStatus === '' ? undefined : variables.trackingStatus 
                    } 
                  };
                  updatedOpportunity = updated;
                  return updated;
                }
                return opp;
              });
              
              // Recalculate stats with updated entities
              const watching = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'watching').length;
              const bidding = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'bidding').length;
              const won = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'won').length;
              const lost = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'lost').length;
              const trackedOpportunities = updatedEntities.filter(
                (o: GovtOpportunityEntity) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
              ).length;
              
              const pipelineValue = updatedEntities
                .filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
                .reduce((sum: number, o: GovtOpportunityEntity) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);
              
              return {
                ...old,
                allEntities: updatedEntities,
                stats: {
                  ...old.stats,
                  watching,
                  bidding,
                  won,
                  lost,
                  trackedOpportunities,
                  pipelineValue,
                },
              };
            }
            return old;
          });
        }
      });
      
      // Update selectedOpportunity if it matches the updated entity
      if (updatedOpportunity && selectedOpportunity?.id === variables.entityId) {
        setSelectedOpportunity(updatedOpportunity);
      }
      
      // Force refetch combined query to recalculate stats (in case of any discrepancies)
      queryClient.invalidateQueries({ 
        queryKey: ['govt-opportunities-all', projectId],
        refetchType: 'active' // Force immediate refetch for active queries
      });
    },
  });

  // Filtered opportunities - now just use the paginated opportunities (filtering/sorting already done above)
  const filteredOpportunities = useMemo(() => {
    // Filtering and sorting are now done in allOpportunities useMemo above
    // This is just for compatibility with existing code that references filteredOpportunities
    return opportunities;
  }, [opportunities]);

  // Calculate statistics - use aggregate stats from API when available
  const stats = useMemo((): OpportunityStats => {
    // Use aggregate stats from API query if available
    const aggregateStats = aggregateStatsData || {
      pipelineValue: 0,
      won: 0,
      lost: 0,
      upcomingDeadlines: 0,
      openOpportunities: 0,
      trackedOpportunities: 0,
      avgDaysUntilDeadline: null,
    };

    // Use stats from combined query (calculated from all entities)
    // For page-level stats (avgMatchScore), use current page
    const matchScores = opportunities.filter((o) => o.data.match_score).map((o) => o.data.match_score!);
    const avgMatchScore = matchScores.length > 0
      ? matchScores.reduce((sum, score) => sum + score, 0) / matchScores.length
      : 0;

    return {
      total: totalCount, // Use totalCount from API
      watching: aggregateStats.watching || 0,
      bidding: aggregateStats.bidding || 0,
      won: aggregateStats.won || 0,
      lost: aggregateStats.lost || 0,
      pipelineValue: aggregateStats.pipelineValue || 0,
      avgMatchScore,
      upcomingDeadlines: aggregateStats.upcomingDeadlines || 0,
      openOpportunities: aggregateStats.openOpportunities || 0,
      trackedOpportunities: aggregateStats.trackedOpportunities || 0,
      avgDaysUntilDeadline: aggregateStats.avgDaysUntilDeadline || null,
    };
  }, [opportunities, totalCount, aggregateStatsData]);

  // Get unique service lines from loaded opportunities
  const serviceLines = useMemo(() => {
    const lines = new Set<string>();
    opportunities.forEach((opp) => {
      if (opp.data.matching_service_lines) {
        opp.data.matching_service_lines.forEach((line) => lines.add(line));
      }
    });
    return Array.from(lines).sort();
  }, [opportunities]);

  // Fetch all sources that have govt_opportunity entities (not just from loaded opportunities)
  const { data: sourcesData } = useQuery({
    queryKey: ['entity-sources', projectId, 'govt_opportunity'],
    queryFn: async () => {
      if (!projectId) return { sources: [], total: 0 };
      return await getEntitySources(projectId, 'govt_opportunity');
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Extract source names/IDs for dropdown - prefer integration_type, fallback to source_name
  const sources = useMemo(() => {
    if (!sourcesData?.sources) return [];
    return sourcesData.sources.map(s => s.integration_type || s.source_name || 'Unknown').filter((v, i, a) => a.indexOf(v) === i).sort();
  }, [sourcesData]);

  // Handler: Update tracking status
  const handleUpdateTracking = (entityId: string, trackingStatus: string) => {
    updateTrackingMutation.mutate({ entityId, trackingStatus });
  };

  // Handler: Export to CSV
  const handleExport = () => {
    const csv = [
      ['Title', 'Organization', 'Status', 'Tracking', 'Deadline', 'Value', 'Source', 'URL'],
      ...filteredOpportunities.map((opp) => [
        opp.data.title || '',
        opp.data.organization || '',
        opp.data.status || '',
        opp.data.tracking_status || '',
        opp.data.deadline || '',
        opp.data.value?.toString() || '',
        opp.data.source || '',
        opp.data.url || '',
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunities-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Pagination handlers
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => {
    if (hasMore) setPage((p) => p + 1);
  };

  // Handler: Auto-match opportunities using new batch vector matching endpoint
  const handleAutoMatch = async () => {
    if (!projectId) return;
    
    setIsMatching(true);
    try {
      const apiClient = getAPIClient();
      
      // Get all opportunity IDs from current results
      const opportunityIds = allOpportunities.map(opp => opp.id);
      
      // Call new batch matching endpoint
      const response = await apiClient.post('/api/opportunities/batch-match', {
        project_id: projectId,
        opportunity_ids: opportunityIds.length > 0 ? opportunityIds : undefined, // If empty, match all
        match_against: 'both',
        limit_per_opportunity: 50,
        min_similarity_threshold: 0.15,
      });
      
      // Store match scores in a Map for quick lookup
      const newMatchScores = new Map<string, number>();
      response.results.forEach((result: any) => {
        newMatchScores.set(result.opportunity_id, result.match_score);
      });
      
      setMatchScores(newMatchScores);
      
      // Enable sorting by match score
      setSortByMatchScore(true);
      
      // Update opportunities data with match scores
      queryClient.setQueryData(['govt-opportunities-all', projectId, searchTerm, filterSource, filterStatus, filterTrackingStatus, filterServiceLine], (oldData: any) => {
        if (!oldData) return oldData;
        
        const updatedEntities = oldData.allEntities.map((entity: any) => {
          const matchScore = newMatchScores.get(entity.id);
          if (matchScore !== undefined) {
            return {
              ...entity,
              data: {
                ...entity.data,
                match_score: matchScore,
              },
            };
          }
          return entity;
        });
        
        return {
          ...oldData,
          allEntities: updatedEntities,
        };
      });
      
      const matchedCount = response.results.filter((r: any) => r.match_score > 0).length;
      alert(`Successfully matched ${formatNumber(matchedCount)} of ${formatNumber(response.total_opportunities)} opportunities in ${(response.processing_time_ms / 1000).toFixed(1)}s!`);
    } catch (error: any) {
      console.error('Auto-match failed:', error);
      alert(`Auto-match failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsMatching(false);
    }
  };

  // Handle search submission (Enter key or search icon click)
  const handleSearch = (value?: string) => {
    const searchValue = value !== undefined ? value : searchInput;
    const trimmed = searchValue.trim();
    
    // Validate query syntax
    const validation = validateSearchQuery(trimmed);
    if (!validation.valid) {
      alert(`Invalid search query: ${validation.error}`);
      return;
    }
    
    setSearchTerm(trimmed);
    setSearchInput(trimmed); // Sync input with search term
    setPage(1); // Reset to first page when searching
    // Save to search history (only if 1-3 words or simple queries)
    if (trimmed) {
      saveToSearchHistory(trimmed);
    }
  };

  // Handle keyword selection from modal
  const handleKeywordSelect = (keyword: string) => {
    setSearchInput(keyword);
    setSearchTerm(keyword);
    setPage(1);
  };

  // Handle Enter key in search input (now handled by SearchAutocomplete)
  // This is kept for backward compatibility but SearchAutocomplete handles it internally

  // Reset page when search term or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterSource, filterStatus, filterTrackingStatus, filterServiceLine]);

  // Don't show full page loading - keep filters visible
  // We'll show progress indicator below search bar instead

  if (!projectId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <p className="text-center py-12 text-gray-500">Select a project to view opportunities</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-theme-text">Government Opportunities Dashboard</h1>
          <p className="text-gray-600 dark:text-theme-text-secondary mt-1">
            Track procurement opportunities from government sources
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* View Mode Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-theme-text">View:</span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={viewMode === 'cards' ? 'primary' : 'outline'}
                onClick={() => setViewMode('cards')}
              >
                <List className="h-4 w-4 mr-1" />
                Cards
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'primary' : 'outline'}
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4 mr-1" />
                List
              </Button>
              {enableCalendarView && (
                <Button
                  size="sm"
                  variant={viewMode === 'calendar' ? 'primary' : 'outline'}
                  onClick={() => setViewMode('calendar')}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Calendar
                </Button>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                onClick={handleAutoMatch}
                disabled={isMatching || allOpportunities.length === 0}
              >
                <Zap className={`h-4 w-4 mr-2 ${isMatching ? 'animate-pulse' : ''}`} />
                {isMatching ? 'Matching...' : 'Auto-Match'}
              </Button>
              <div className="relative group">
                <HelpCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-white dark:bg-gray-800 border border-theme-border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  <p className="text-sm text-theme-text">
                    <strong>Auto-Match:</strong> Rank opportunities by similarity to your past projects and capability statements using AI-powered semantic matching. Results are reordered by match score.
                  </p>
                </div>
              </div>
              {sortByMatchScore && matchScores.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortByMatchScore(false)}
                  title="Return to original order"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear Sort
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 hover:shadow-lg transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">Total Opportunities</p>
              <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-theme-text">{formatNumber(stats.total)}</p>
            </div>
            <Target className="h-8 w-8 text-blue-500 dark:text-blue-400" />
          </div>
        </Card>

        <Card className="p-4 hover:shadow-lg transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">Open Opportunities</p>
              <p className="text-xs text-gray-500 dark:text-theme-text-muted mt-1">Currently Active</p>
              <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">
                {formatNumber(stats.openOpportunities)}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500 dark:text-green-400" />
          </div>
        </Card>

        <Card className="p-4 hover:shadow-lg transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">Being Tracked</p>
              <p className="text-xs text-gray-500 dark:text-theme-text-muted mt-1">Watching + Bidding</p>
              <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">
                {formatNumber(stats.trackedOpportunities)}
              </p>
            </div>
            <Eye className="h-8 w-8 text-blue-500 dark:text-blue-400" />
          </div>
        </Card>

        <Card className="p-4 hover:shadow-lg transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">Upcoming Deadlines</p>
              <p className="text-xs text-gray-500 dark:text-theme-text-muted mt-1">Next 30 days</p>
              <p className="text-2xl font-bold mt-1 text-orange-600 dark:text-orange-400">
                {formatNumber(stats.upcomingDeadlines)}
              </p>
            </div>
            <Clock className="h-8 w-8 text-orange-500 dark:text-orange-400" />
          </div>
        </Card>
      </div>


      {/* Search and Filters - Collapsible */}
      <Card className="p-4 mb-6">
        <div className="space-y-3">
          {/* Search Bar with Icon on Left, Filters on Right */}
          <div className="flex items-center gap-3">
            {/* Search Input with Autocomplete - Takes remaining space */}
            <div className="flex-1 relative">
              <SearchAutocomplete
                value={searchInput}
                onChange={setSearchInput}
                onSelect={handleSearch}
                searchHistory={searchHistory}
                projectId={projectId}
                placeholder="Search opportunities... (Use AND, OR, NOT, or field:value syntax)"
                className="bg-theme-paper text-theme-text border-theme-border"
              />
            </div>
            
            {/* Search Button - Clear call-to-action */}
            <Button
              type="button"
              onClick={() => handleSearch()}
              variant="primary"
              size="sm"
              className="flex-shrink-0"
              aria-label="Search"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            
            {/* Search Info Tooltip */}
            <div className="flex-shrink-0">
              <SearchInfoTooltip />
            </div>
            
            {/* Keyword Recommendations Button */}
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => setShowKeywordModal(true)}
                className="flex items-center gap-2"
                title="Get AI-recommended search keywords"
              >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Keywords</span>
              </Button>
            </div>
            
            {/* Filters Button on Right */}
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                {filtersExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Search Progress Indicator - Shows during vector search */}
          {isLoading && searchTerm && (
            <div className="mt-4">
              <SearchProgressIndicator
                isSearching={isLoading}
                stage="calculating" // Show "Calculating Similarities" stage for semantic search
                message={
                  searchTerm
                    ? `Finding opportunities matching "${searchTerm}"...`
                    : 'Searching opportunities...'
                }
              />
            </div>
          )}

          {/* Collapsible Filters */}
          {filtersExpanded && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-theme-border">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-theme-text">Source</label>
                  <select
                    value={filterSource}
                    onChange={(e) => setFilterSource(e.target.value)}
                    className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="all">All Sources</option>
                    {sources.map((src) => (
                      <option key={src} value={src}>
                        {src}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-theme-text">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="awarded">Awarded</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-theme-text">Tracking</label>
                  <select
                    value={filterTrackingStatus}
                    onChange={(e) => setFilterTrackingStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="all">All Tracking</option>
                    <option value="watching">Watching</option>
                    <option value="bidding">Bidding</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-theme-text">Service Line</label>
                  <select
                    value={filterServiceLine}
                    onChange={(e) => setFilterServiceLine(e.target.value)}
                    className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="all">All Service Lines</option>
                    {serviceLines.map((line) => (
                      <option key={line} value={line}>
                        {line}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Match Score Filter - Only show if match scores are available */}
                {matchScores.size > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-theme-text">Match Score</label>
                    <select
                      value={filterMatchScore}
                      onChange={(e) => setFilterMatchScore(e.target.value)}
                      className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="all">All Matches</option>
                      <option value="high">High Match (80%+)</option>
                      <option value="medium">Medium Match (50-79%)</option>
                      <option value="low">Low Match (&lt;50%)</option>
                    </select>
                  </div>
                )}
            </div>
          )}

          {/* Results Count */}
          <div className="text-sm text-gray-600 dark:text-theme-text-secondary pt-2 border-t border-theme-border">
            Showing {filteredOpportunities.length} of {totalCount} opportunities
          </div>
        </div>
      </Card>

      {/* Calendar View */}
      {viewMode === 'calendar' && enableCalendarView && (
        <Card className="p-4 mb-6">
          {calendarLocalizer ? (
            <div style={{ height: '600px' }}>
              <BigCalendar
                localizer={calendarLocalizer}
              events={filteredOpportunities
                .filter((opp) => opp.data.deadline)
                .map((opp) => ({
                  id: opp.id,
                  title: opp.data.title || 'Untitled',
                  start: new Date(opp.data.deadline!),
                  end: new Date(opp.data.deadline!),
                  resource: opp,
                }))}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectEvent={(event: Event & { resource?: GovtOpportunityEntity }) => {
                if (event.resource) {
                  setSelectedOpportunity(event.resource);
                  setIsModalOpen(true);
                }
              }}
              popup
                className="bg-white dark:bg-theme-paper"
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-theme-text">Loading calendar...</p>
            </div>
          )}
        </Card>
      )}

      {/* Deadline Calendar (optional view) - DEPRECATED */}
      {false && (
        <Card className="p-4 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Deadlines
          </h3>
          <div className="space-y-2">
            {filteredOpportunities
              .filter((opp) => opp.data.deadline)
              .sort((a, b) => {
                const dateA = new Date(a.data.deadline!).getTime();
                const dateB = new Date(b.data.deadline!).getTime();
                return dateA - dateB;
              })
              .slice(0, 10)
              .map((opp) => {
              const daysUntil = opp.data.days_until_deadline ?? null;
              const isUrgent = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
              const isExpired = daysUntil !== null && daysUntil < 0;

                return (
                  <div
                    key={opp.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      isUrgent
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-gray-900 dark:text-theme-text'
                        : isExpired
                        ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-theme-text'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-gray-900 dark:text-theme-text'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">{opp.data.title}</p>
                      <p className="text-xs text-gray-600 dark:text-theme-text-secondary">{opp.data.organization}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">
                        {new Date(opp.data.deadline!).toLocaleDateString()}
                      </p>
                      <p
                        className={`text-xs ${
                          isUrgent
                            ? 'text-red-600 font-semibold'
                            : isExpired
                            ? 'text-gray-500'
                            : 'text-blue-600'
                        }`}
                      >
                        {isExpired
                          ? `${Math.abs(daysUntil!)} days overdue`
                          : daysUntil === 0
                          ? 'Due today!'
                          : `${daysUntil} days left`}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
      )}

      {/* Opportunities Grid/List - Only show if not calendar view */}
      {viewMode !== 'calendar' && (
        isLoading ? (
          <Card>
            <div className="text-center py-12">
              <div className="flex flex-col items-center justify-center">
                <RefreshCw className="h-12 w-12 mx-auto text-primary-600 dark:text-primary-400 mb-4 animate-spin" />
                <p className="text-gray-600 dark:text-theme-text font-medium">
                  {searchTerm 
                    ? `Searching for opportunities matching "${searchTerm}"...`
                    : 'Loading opportunities...'}
                </p>
                <p className="text-sm text-gray-500 dark:text-theme-text-secondary mt-2">
                  Please wait while we fetch the latest data
                </p>
              </div>
            </div>
          </Card>
        ) : filteredOpportunities.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-theme-text">No opportunities found</p>
              <p className="text-sm text-gray-500 dark:text-theme-text-secondary mt-2">
                {searchTerm || filterSource !== 'all' || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'No opportunities have been loaded yet'}
              </p>
            </div>
          </Card>
        ) : (
        <>
          <div className={`grid gap-6 mb-6 ${
            viewMode === 'cards' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredOpportunities.map((opp) => {
              // Enhance opportunity with computed fields
              const enhancedOpp = enhanceOpportunityEntity(opp);
              
              // Add match score from state if available
              const matchScore = matchScores.get(opp.id);
              if (matchScore !== undefined) {
                enhancedOpp.data.match_score = matchScore;
              }
              
              const daysUntil = enhancedOpp.data.days_until_deadline ?? null;
              const urgency = getDeadlineUrgency(daysUntil);
              const isUrgent = urgency === 'urgent';
              const isExpired = urgency === 'expired';
              const isSoon = urgency === 'soon';
              
              // Get opportunity value
              const opportunityValue = getOpportunityValue(enhancedOpp.data);
              const projectId = getProjectIdDisplay(enhancedOpp);
              const stage = enhancedOpp.data.proposal_stage || 'Unknown';
              const duration = enhancedOpp.data.duration_display || 'Not specified';

              return (
                <div
                  key={opp.id}
                  className="cursor-pointer"
                  onClick={(e) => {
                    console.log('[OpportunitiesDashboard] Card wrapper clicked:', {
                      opportunityId: opp.id,
                      title: opp.data.title,
                      eventTarget: e.target,
                      currentTarget: e.currentTarget,
                      isModalOpenBefore: isModalOpen,
                      selectedOpportunityBefore: selectedOpportunity?.id,
                    });
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedOpportunity(enhancedOpp);
                    setIsModalOpen(true);
                    console.log('[OpportunitiesDashboard] State updated:', {
                      isModalOpenAfter: true,
                      selectedOpportunityAfter: opp.id,
                    });
                  }}
                >
                <Card
                  className={`p-4 hover:shadow-lg transition-shadow duration-200 flex flex-col border ${
                    enhancedOpp.data.tracking_status === 'watching'
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                      : enhancedOpp.data.tracking_status === 'bidding'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                      : enhancedOpp.data.tracking_status === 'won'
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700'
                      : enhancedOpp.data.tracking_status === 'lost'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                      : enhancedOpp.data.tracking_status === 'declined'
                      ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-700'
                      : 'bg-white dark:bg-theme-paper border-theme-border'
                  }`}
                >
                  {/* Header with Project ID and Match Score */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-lg line-clamp-2 text-theme-text">{enhancedOpp.data.title}</h3>
                        {/* Relevance Score Badge from Vector Search - HIDDEN: Only show Auto-Match score */}
                        {/* Tracking Status Badge */}
                        {enhancedOpp.data.tracking_status && (
                          <Badge
                            variant={
                              enhancedOpp.data.tracking_status === 'watching'
                                ? 'default'
                                : enhancedOpp.data.tracking_status === 'bidding'
                                ? 'success'
                                : enhancedOpp.data.tracking_status === 'won'
                                ? 'success'
                                : enhancedOpp.data.tracking_status === 'lost'
                                ? 'error'
                                : 'default'
                            }
                            className={
                              enhancedOpp.data.tracking_status === 'watching'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                                : enhancedOpp.data.tracking_status === 'bidding'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'
                                : enhancedOpp.data.tracking_status === 'won'
                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700'
                                : enhancedOpp.data.tracking_status === 'lost'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
                                : ''
                            }
                          >
                            {enhancedOpp.data.tracking_status.charAt(0).toUpperCase() + enhancedOpp.data.tracking_status.slice(1)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-theme-text-secondary mb-2">
                        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                          ID: {projectId}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-theme-text-secondary mb-2">
                        <Building2 className="h-4 w-4 flex-shrink-0" />
                        <span className="line-clamp-1">{enhancedOpp.data.organization || enhancedOpp.data.issuing_agency || 'Unknown'}</span>
                      </div>
                    </div>
                    {(enhancedOpp.data.match_score !== undefined && enhancedOpp.data.match_score !== null) || matchScores.get(opp.id) !== undefined ? (
                      <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                        {(() => {
                          const score = enhancedOpp.data.match_score ?? matchScores.get(opp.id) ?? 0;
                          const scoreColor = score >= 0.8
                            ? 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                            : score >= 0.5
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300'
                            : 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300';
                          return (
                            <div className={`flex items-center gap-1 px-2 py-1 rounded-full border ${scoreColor}`} title={`Auto-Match Score: ${(score * 100).toFixed(1)}%`}>
                              <Star className="h-3 w-3 fill-current" />
                              <span className="text-xs font-semibold">
                                {(score * 100).toFixed(0)}%
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    ) : null}
                  </div>

                  {/* Key Stats Row - Stage, Value, Duration */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {/* Proposal Stage Badge */}
                    {stage !== 'Unknown' && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStageBadgeColor(stage)}`}>
                        {stage}
                      </span>
                    )}
                    
                    {/* Dollar Value */}
                    {opportunityValue !== null && (
                      <div className="flex items-center gap-1 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-md border border-green-200 dark:border-green-800">
                        <DollarSign className="h-3 w-3 text-green-600 dark:text-green-400" />
                        <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                          {formatCurrency(opportunityValue, { compact: true, symbol: enhancedOpp.data.currency === 'CAD' ? 'C$' : '$' })}
                        </span>
                      </div>
                    )}
                    
                    {/* Duration */}
                    {duration !== 'Not specified' && (
                      <div className="flex items-center gap-1 text-xs text-theme-text-secondary">
                        <Clock className="h-3 w-3" />
                        <span>{duration}</span>
                      </div>
                    )}
                  </div>

                  {/* Status Badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge
                      variant={
                        enhancedOpp.data.status === 'open'
                          ? 'success'
                          : enhancedOpp.data.status === 'closed'
                          ? 'default'
                          : 'warning'
                      }
                    >
                      {enhancedOpp.data.status || 'Unknown'}
                    </Badge>
                    {enhancedOpp.data.source && (
                      <Badge variant="default">
                        <Tag className="h-3 w-3 mr-1" />
                        {enhancedOpp.data.source}
                      </Badge>
                    )}
                  </div>

                  {/* Deadline with Urgency Indicator */}
                  {enhancedOpp.data.deadline && (
                    <div className={`mb-3 p-2 rounded-lg border ${
                      isUrgent
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : isExpired
                        ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                        : isSoon
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock
                            className={`h-4 w-4 ${
                              isUrgent ? 'text-red-600 dark:text-red-400' : 
                              isExpired ? 'text-gray-400' : 
                              isSoon ? 'text-yellow-600 dark:text-yellow-400' : 
                              'text-blue-600 dark:text-blue-400'
                            }`}
                          />
                          <span className="text-xs font-medium text-theme-text">
                            Deadline
                          </span>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-semibold ${
                            isUrgent
                              ? 'text-red-700 dark:text-red-300'
                              : isExpired
                              ? 'text-gray-500 dark:text-gray-400'
                              : isSoon
                              ? 'text-yellow-700 dark:text-yellow-300'
                              : 'text-blue-700 dark:text-blue-300'
                          }`}>
                            {new Date(enhancedOpp.data.deadline).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                          <div className={`text-xs ${
                            isUrgent
                              ? 'text-red-600 dark:text-red-400'
                              : isExpired
                              ? 'text-gray-500 dark:text-gray-400'
                              : isSoon
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`}>
                            {isExpired
                              ? `Expired ${Math.abs(daysUntil!)} days ago`
                              : daysUntil === 0
                              ? 'Due today!'
                              : daysUntil === 1
                              ? '1 day left'
                              : `${daysUntil} days left`}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {enhancedOpp.data.description && (
                    <p className="text-sm text-theme-text-secondary mb-3 line-clamp-3">
                      {enhancedOpp.data.description}
                    </p>
                  )}

                  {/* Additional Metadata */}
                  <div className="space-y-2 mb-3 flex-1">
                    {enhancedOpp.data.location && (
                      <div className="flex items-center gap-2 text-sm text-theme-text-secondary">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="line-clamp-1">{enhancedOpp.data.location}</span>
                      </div>
                    )}
                    {enhancedOpp.data.matching_service_lines && enhancedOpp.data.matching_service_lines.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {enhancedOpp.data.matching_service_lines.slice(0, 2).map((line) => (
                          <span
                            key={line}
                            className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded border border-blue-200 dark:border-blue-800"
                          >
                            {line}
                          </span>
                        ))}
                        {enhancedOpp.data.matching_service_lines.length > 2 && (
                          <span className="text-xs text-theme-text-secondary">
                            +{enhancedOpp.data.matching_service_lines.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tracking Status Selector */}
                  <div 
                    className="mb-3" 
                    onClick={(e) => {
                      console.log('[OpportunitiesDashboard] Tracking selector clicked, stopping propagation');
                      e.stopPropagation();
                    }}
                  >
                    <label className="block text-xs font-medium text-gray-700 dark:text-theme-text mb-1">
                      Tracking Status
                    </label>
                    <select
                      value={enhancedOpp.data.tracking_status || ''}
                      onChange={(e) => {
                        console.log('[OpportunitiesDashboard] Tracking status changed:', e.target.value);
                        e.stopPropagation();
                        handleUpdateTracking(opp.id, e.target.value);
                      }}
                      onClick={(e) => {
                        console.log('[OpportunitiesDashboard] Select clicked, stopping propagation');
                        e.stopPropagation();
                      }}
                      className="w-full px-2 py-1 text-sm border border-theme-border rounded bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="">Not tracking</option>
                      <option value="watching">Watching</option>
                      <option value="bidding">Bidding</option>
                      <option value="won">Won</option>
                      <option value="lost">Lost</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2" onClick={(e) => {
                    console.log('[OpportunitiesDashboard] Actions div clicked, stopping propagation');
                    e.stopPropagation();
                  }}>
                    {opp.data.url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          console.log('[OpportunitiesDashboard] View button clicked');
                          e.stopPropagation();
                          e.preventDefault();
                          window.open(opp.data.url, '_blank');
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    )}
                    <Button 
                      variant="primary" 
                      size="sm" 
                      className="flex-1"
                      onClick={(e) => {
                        console.log('[OpportunitiesDashboard] Proposal button clicked');
                        e.stopPropagation();
                        e.preventDefault();
                        setProposalOpportunity(opp);
                        setShowProposalPreview(true);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Proposal
                    </Button>
                  </div>
                </Card>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-theme-text-secondary">
                Page {page} of {Math.ceil(totalCount / pageSize)} • Total: {totalCount} opportunities
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={handleNextPage} disabled={!hasMore}>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </>
        )
      )}

      {/* Proposal Builder Modal */}
      <Modal
        isOpen={showProposalPreview}
        onClose={() => {
          setShowProposalPreview(false);
          setProposalOpportunity(null);
        }}
        title="Generate Proposal"
        size="xl"
      >
        {proposalOpportunity && (
          <ProposalBuilder
            opportunityId={proposalOpportunity.id}
            opportunityTitle={proposalOpportunity.data.title || 'Untitled Opportunity'}
            clientName={proposalOpportunity.data.issuing_agency || proposalOpportunity.data.organization || 'Government Agency'}
            onComplete={(jobId) => {
              setShowProposalPreview(false);
              setProposalOpportunity(null);
              // Navigate to reports page or show success message
              queryClient.invalidateQueries({ queryKey: ['reports'] });
              queryClient.invalidateQueries({ queryKey: ['report-jobs'] });
            }}
            onCancel={() => {
              setShowProposalPreview(false);
              setProposalOpportunity(null);
            }}
          />
        )}
      </Modal>

      {/* Opportunity Detail Modal */}
      <OpportunityDetailModal
        opportunity={selectedOpportunity}
        isOpen={isModalOpen}
        onClose={() => {
          console.log('[OpportunitiesDashboard] Modal onClose called');
          setIsModalOpen(false);
          setSelectedOpportunity(null);
        }}
        onSaveToList={(listId) => {
          if (selectedOpportunity) {
            // TODO: Implement save to list
            console.log('Save to list:', listId, selectedOpportunity.id);
          }
        }}
        onUpdateTracking={handleUpdateTracking}
        onOpportunityUpdate={(updatedOpportunity) => {
          // Update the selected opportunity state immediately for instant UI feedback
          setSelectedOpportunity(updatedOpportunity);
        }}
        onGenerateProposal={() => {
          if (selectedOpportunity) {
            setIsModalOpen(false);
            setProposalOpportunity(selectedOpportunity);
            setShowProposalPreview(true);
          }
        }}
      />

      {/* Keyword Recommendations Modal */}
      <KeywordRecommendationsModal
        isOpen={showKeywordModal}
        onClose={() => setShowKeywordModal(false)}
        onSelectKeyword={handleKeywordSelect}
      />
    </div>
  );
}

